import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Parse a stat text like "20+", "5923+", "98%" into numeric + suffix.
 * @param {string} text
 * @returns {{ value: number, suffix: string, decimals: number } | null}
 */
function parseStatValue(text) {
	const raw = text.trim();
	const match = raw.match(/^([\d.,]+)(.*)$/);

	if (!match) {
		return null;
	}

	const numberPart = match[1].replace(/,/g, '');
	const value = Number.parseFloat(numberPart);

	if (Number.isNaN(value)) {
		return null;
	}

	const decimalSegment = numberPart.includes('.') ? numberPart.split('.')[1] : '';

	return {
		value,
		suffix: match[2] ?? '',
		decimals: decimalSegment.length,
	};
}

/**
 * @param {HTMLElement} el
 * @param {{ value: number, suffix: string, decimals: number }} parsed
 * @param {number} current
 */
function formatStatValue(el, parsed, current) {
	const { suffix, decimals } = parsed;
	const display =
		decimals > 0 ? current.toFixed(decimals) : String(Math.round(current));
	el.textContent = `${display}${suffix}`;
}

/**
 * Animate all `.js-stat-value` elements inside the given section.
 * @param {HTMLElement} section
 */
function animateStatsSection(section) {
	if (section.dataset.statsAnimated === 'true') {
		return;
	}

	// Claim the section up front, not in onComplete. ScrollTrigger.create()
	// already fires onEnter synchronously when the section sits inside the
	// trigger zone at load, and the isActive check in initStatsCountUp() then
	// called this a second time — before onComplete had marked it done. The
	// second gsap.from() reads each element's *current* opacity as its end
	// value, and the first timeline had already pulled them toward 0, so the
	// numbers stayed permanently stuck at whatever alpha it had reached
	// (measured 0.70 / 0.08 / 0.04, faded left-to-right by the stagger) — the
	// washed-out figures in #182. Only pages where the strip is above the fold
	// were affected.
	section.dataset.statsAnimated = 'true';

	const statValues = section.querySelectorAll('.js-stat-value');
	const targets = [];

	statValues.forEach((el) => {
		const finalText = (el.dataset.statFinal ?? el.textContent).trim();
		const parsed = parseStatValue(finalText);

		if (!parsed) {
			return;
		}

		el.dataset.statFinal = finalText;
		targets.push({ el, parsed });
	});

	if (!targets.length) {
		return;
	}

	const timeline = gsap.timeline({
		defaults: { ease: 'power2.out' },
	});

	targets.forEach(({ el, parsed }, index) => {
		const counter = { value: 0 };

		// Start from 0
		el.textContent = `0${parsed.suffix}`;

		timeline
			.from(
				el,
				{
					y: 16,
					autoAlpha: 0,
					duration: 0.5,
					ease: 'power3.out',
				},
				index * 0.15,
			)
			.to(
				counter,
				{
					value: parsed.value,
					duration: 1.4,
					ease: 'power2.out',
					onUpdate: () => formatStatValue(el, parsed, counter.value),
				},
				index * 0.15,
			);
	});
}

export function initStatsCountUp() {
	const sections = document.querySelectorAll('.section--stats');

	if (!sections.length) {
		return;
	}

	gsap.matchMedia().add('(prefers-reduced-motion: no-preference)', () => {
		const triggers = [];

		sections.forEach((section) => {
			const trigger = ScrollTrigger.create({
				trigger: section,
				start: 'top 85%',
				once: true,
				invalidateOnRefresh: true,
				onEnter: () => animateStatsSection(section),
			});

			// Safety net for the case where creating the trigger did not already
			// fire onEnter. animateStatsSection() is idempotent, so this cannot
			// start a second, competing timeline.
			if (trigger.isActive) {
				animateStatsSection(section);
			}

			triggers.push(trigger);
		});

		return () => {
			triggers.forEach((t) => t.kill());
		};
	});

	// Fallback: if reduced-motion is preferred, show final values statically
	gsap.matchMedia().add('(prefers-reduced-motion: reduce)', () => {
		// Values already in DOM as static text — nothing to do
	});
}
